import { useState, useCallback, useEffect } from 'react';

import { useGoogleAnalyticsEventTracker } from '@vvoyages/shared-ui/GoogleAnalytics';

import { useAuth } from '@/helpers/authUtils';
import { requiresReactivation } from '@/helpers/signinUtils';
import { useSignInLogic } from '@/hooks/useSignInLogic';
import { useSocialSignupForm } from '@/hooks/useSocialSignupForm';

interface UseSignInPageConfig {
  redirectOnSuccess: (_session: any) => void | Promise<void>;
  checkAuthRedirect?: () => Promise<void>;
  isFromIframe?: boolean;
}

export const useSignInPage = ({ redirectOnSuccess, checkAuthRedirect, isFromIframe = false }: UseSignInPageConfig) => {
  // Error and UI state management
  const [showErrorBox, setShowErrorBox] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string[]>([]);
  const [serverErrorMsg, setServerErrorMsg] = useState<string[]>([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isRedirectedFromSocialLogin, setRedirectedFromSocialLogin] = useState(false);
  const trackEvent = useGoogleAnalyticsEventTracker();
  const Auth = useAuth();

  // Authentication logic
  const {
    handleSignIn,
    handleSocialLogin,
    handleSocialSignup,
    exitReactivationFlow,
    isShowReactivationFlow,
    client: reactivationClient,
    claimBookingCredentials,
  } = useSignInLogic(isFromIframe);

  // Social signup form management
  const socialSignupForm = useSocialSignupForm();

  // Context value for child components
  const contextValue = {
    showErrorBox,
    setShowErrorBox,
    errorMsg,
    setErrorMsg,
    serverErrorMsg,
    setServerErrorMsg,
    isSubmitDisabled,
    setIsSubmitDisabled,
    isFromIframe,
  };

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!Auth) return;

      const isLoggedIn = await Auth?.isAuth();
      if (isLoggedIn) {
        if (checkAuthRedirect) {
          await checkAuthRedirect();
        } else {
          const session = await Auth?.getAuth();
          if (!requiresReactivation(session)) {
            await redirectOnSuccess(session);
          }
        }
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Auth]);

  // Handle main sign-in form submission
  const handleSignInSubmit = useCallback(
    async ({ email, password }: any) => {
      try {
        setShowErrorBox(false);
        const result = await handleSignIn({ email, password });

        if (result.success) {
          trackEvent('login', {
            method: 'Direct',
            user_data: { email: email?.trim() },
          });

          // eslint-disable-next-line no-debugger
          await redirectOnSuccess(result.session);
        }
      } catch {
        setShowErrorBox(true);
      }
    },
    [handleSignIn, redirectOnSuccess, trackEvent],
  );

  // Handle social login wrapper
  const handleSocialLoginWrapper = useCallback(
    async (formFieldValues: any) => {
      setRedirectedFromSocialLogin(false);
      setServerErrorMsg([]);
      setShowErrorBox(false);

      // Update social form state
      socialSignupForm.updateFormState(formFieldValues);

      try {
        const result = await handleSocialLogin(formFieldValues);

        if (result.success) {
          const { type, email } = formFieldValues;

          trackEvent('login', {
            method: type,
            user_data: { email: email?.trim() },
          });

          await redirectOnSuccess(result.session);
        } else if (result.requiresSignup) {
          setRedirectedFromSocialLogin(true);
        }
      } catch {
        setErrorMsg(['Something went wrong']);
        setShowErrorBox(true);
      }
    },
    [handleSocialLogin, redirectOnSuccess, socialSignupForm, trackEvent],
  );

  // Handle social signup confirmation
  const handleSocialSignupConfirm = useCallback(
    async (formData: any) => {
      setShowErrorBox(false);

      try {
        const result = await handleSocialSignup(formData);

        if (result?.success) {
          const { type, email } = formData;

          trackEvent('login', {
            method: type,
            user_data: { email: email?.trim() },
          });

          await redirectOnSuccess(result.session);
        }
      } catch {
        setErrorMsg(['Something went wrong']);
        setShowErrorBox(true);
      }
    },
    [handleSocialSignup, redirectOnSuccess, trackEvent],
  );

  return {
    contextValue,
    isShowReactivationFlow,
    reactivationClient,
    exitReactivationFlow,
    isRedirectedFromSocialLogin,
    handleSignInSubmit,
    handleSocialLoginWrapper,
    handleSocialSignupConfirm,
    socialFormState: socialSignupForm.formFieldsState,
    claimBookingCredentials,
  };
};
